import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Package,
  FolderTree,
  Tag,
  LayoutTemplate,
  Percent,
  Star,
  Images,
  SlidersHorizontal,
  ShoppingCart,
  CreditCard,
  Truck,
  Mail,
  BarChart3,
  PanelBottom,
  Menu,
  LogOut,
  ExternalLink,
  KeyRound,
  UserCircle2,
  UploadCloud,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAdminAuth } from "../AdminAuthContext";
import { useEditorGuard, canLeave } from "../EditorGuard";
import { apiChangePassword, errMsg } from "../adminApi";
import { EnvironmentBadge } from "./EnvironmentBadge";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/statistiche", label: "Statistiche", icon: BarChart3 },
  { to: "/admin/ordini", label: "Ordini", icon: ShoppingCart },
  { to: "/admin/pagamenti", label: "Pagamenti", icon: CreditCard },
  { to: "/admin/spedizioni", label: "Spedizioni GLS", icon: Truck },
  { to: "/admin/recupero-carrelli", label: "Recupero carrelli", icon: Mail },
  { to: "/admin/report-meta", label: "Report Meta", icon: BarChart3 },
  { to: "/admin/homepage", label: "Homepage", icon: LayoutTemplate },
  { to: "/admin/hero", label: "Hero", icon: Images },
  { to: "/admin/media-sezioni", label: "Media sezioni", icon: SlidersHorizontal },
  { to: "/admin/prodotti", label: "Prodotti", icon: Package },
  { to: "/admin/offerte", label: "Offerte", icon: Percent },
  { to: "/admin/recensioni", label: "Recensioni", icon: Star },
  { to: "/admin/categorie", label: "Categorie", icon: FolderTree },
  { to: "/admin/marchi", label: "Marchi", icon: Tag },
  { to: "/admin/footer", label: "Footer", icon: PanelBottom },
  { to: "/admin/pubblicazione", label: "Pubblicazione", icon: UploadCloud },
];

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1" aria-label="Navigazione admin">
      {NAV.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`sidebar-nav-${item.label.toLowerCase()}-link`}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150",
                isActive
                  ? "bg-surface3 text-lime"
                  : "text-steel hover:bg-surface2 hover:text-chalk",
              ].join(" ")
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function SidebarInner({ onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <div className="px-3 py-5">
        <div className="px-3">
          <span className="font-display text-lg font-extrabold tracking-tight text-chalk">RINOVA</span>
          <span className="ml-2 text-xs font-medium text-steel">Admin</span>
        </div>
      </div>
      <div className="flex-1 px-3">
        <NavItems onNavigate={onNavigate} />
      </div>
      <div className="px-3 pb-4 pt-2 text-xs text-tertiary">
        <p>Sezione riservata</p>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, doLogout } = useAdminAuth();
  const { dirty, setDirty } = useEditorGuard();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  // Guardia navigazione: blocca i link della sidebar se ci sono modifiche non salvate.
  const guardNavigate = (e) => {
    if (!canLeave(dirty)) {
      e.preventDefault();
      return;
    }
    setDirty(false);
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    if (!canLeave(dirty)) return;
    setDirty(false);
    await doLogout();
    navigate("/admin/login", { replace: true });
  };

  const submitPassword = async () => {
    if (newPw.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri.");
      return;
    }
    setPwSaving(true);
    try {
      await apiChangePassword(curPw, newPw);
      toast.success("Password aggiornata");
      setPwOpen(false);
      setCurPw(""); setNewPw("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink">
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[248px] border-r border-line bg-surface lg:block">
        <SidebarInner onNavigate={guardNavigate} />
      </aside>

      {/* Content */}
      <div className="lg:pl-[248px]">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-line bg-surface/95 px-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  data-testid="admin-mobile-menu-button"
                  aria-label="Apri menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[260px] border-line bg-surface p-0">
                <SheetHeader className="sr-only">
                  <SheetTitle>Menu admin</SheetTitle>
                </SheetHeader>
                <SidebarInner onNavigate={guardNavigate} />
              </SheetContent>
            </Sheet>
            <span className="font-display text-sm font-bold tracking-tight text-chalk lg:hidden">RINOVA Admin</span>
          </div>

          <div className="flex items-center gap-2">
            <EnvironmentBadge className="hidden sm:inline-flex" />
            <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site-link">
              <Button variant="ghost" size="sm" className="text-steel hover:text-chalk">
                <ExternalLink className="h-4 w-4" /> <span className="hidden sm:inline">Vedi sito</span>
              </Button>
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" data-testid="admin-user-menu">
                  <UserCircle2 className="h-4 w-4" />
                  <span className="hidden max-w-[160px] truncate sm:inline" data-testid="admin-current-user">{user?.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="admin-change-password-item">
                  <KeyRound className="h-4 w-4" /> Cambia password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="admin-logout-button">
                  <LogOut className="h-4 w-4" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>

      {/* Cambia password */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambia password</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cur-pw">Password attuale</Label>
              <Input id="cur-pw" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="current-password-input" autoComplete="current-password" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-pw">Nuova password</Label>
              <Input id="new-pw" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" autoComplete="new-password" />
              <p className="text-xs text-tertiary">Almeno 8 caratteri.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPwOpen(false)}>Annulla</Button>
            <Button variant="lime" onClick={submitPassword} disabled={pwSaving} data-testid="change-password-submit">Aggiorna</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
